import React, { useEffect, useMemo, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { toast } from "sonner";
import { api, uploadFile, uploadAndCreatePost, publicAssetUrl } from "@/lib/api";
import { POST_TYPE_LABEL, POST_TYPES } from "@/lib/labels";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Upload, Sparkles, Save, Send } from "lucide-react";
import PartnerLinkSection from "@/components/posts/PartnerLinkSection";
import YouTubeTrailerSection from "@/components/posts/YouTubeTrailerSection";
import OfferGallery from "@/components/posts/OfferGallery";

const EXHIBITION_TYPES = ["PROPERTY", "EDUCATION", "GOVERNMENT", "WEEKEND_MARKET", "OTHER"];

// Backend PostCreateRequest is a discriminated union — each type only allows
// a specific set of `details` keys. React Hook Form keeps every registered
// field in state even after the input unmounts, so we filter here on submit.
const DETAILS_WHITELIST = {
    OFFER: ["discount_percent", "price_before", "price_after", "offer_code", "terms", "business_type", "partner_url", "partner_brand_slug", "gallery"],
    MOVIE: ["movie_title", "theatre_name", "showtimes", "language", "certification", "partner_url", "partner_brand_slug", "trailer_youtube_url"],
    EVENT: ["venue", "event_datetime", "organizer", "ticket_price"],
    JOB_FAIR: ["organizer", "venue", "event_datetime", "roles", "contact"],
    EXHIBITION: ["exhibition_type", "venue", "start_date", "end_date", "organizer"],
};

const pickDetailsForType = (type, raw) => {
    const keep = DETAILS_WHITELIST[type] || [];
    const out = {};
    for (const k of keep) {
        const v = raw?.[k];
        if (v !== undefined && v !== null && v !== "") out[k] = v;
    }
    // Always persist gallery as an array (empty means "clear") so edits can remove images.
    if (type === "OFFER" && Array.isArray(raw?.gallery)) {
        out.gallery = raw.gallery;
    }
    return out;
};

const toIsoLocal = (v) => (v ? new Date(v).toISOString() : null);

export const PostForm = ({ categories, districts, onSubmitted, editing }) => {
    const { user } = useAuth();
    const isShopOwner = user?.role === "SHOP_OWNER";
    const isStaff = user?.role === "STAFF";

    const {
        register,
        control,
        handleSubmit,
        watch,
        setValue,
        formState: { errors, isSubmitting },
    } = useForm({
        defaultValues: {
            type: editing?.type || (isShopOwner ? "OFFER" : "OFFER"),
            title: editing?.title || "",
            description: editing?.description || "",
            district_slug: editing?.district_slug || (isShopOwner ? user?.business_district : ""),
            category_slug: editing?.category_slug || (isShopOwner ? user?.business_category : ""),
            valid_from: editing?.valid_from ? editing.valid_from.slice(0, 16) : "",
            valid_to: editing?.valid_to ? editing.valid_to.slice(0, 16) : "",
            details: editing?.details || {},
        },
    });

    const type = watch("type");
    const [file, setFile] = useState(null);
    const [preview, setPreview] = useState(editing?.poster_image_url ? publicAssetUrl(editing.poster_image_url) : null);
    const [status, setStatus] = useState("PUBLISHED"); // PUBLISHED | DRAFT
    useEffect(() => {
        if (isStaff) setStatus("DRAFT");
    }, [isStaff]);

    const onFileChange = (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        setFile(f);
        setPreview(URL.createObjectURL(f));
    };

    const submit = async (values) => {
        try {
            // Strip out any details keys that don't belong to `values.type`
            // (react-hook-form retains all previously registered fields across
            // type-switches, which caused 422 extra_forbidden on the backend).
            let details = pickDetailsForType(values.type, values.details);

            if (values.type === "OFFER" && details.discount_percent != null && details.discount_percent !== "") {
                const n = Number(details.discount_percent);
                details.discount_percent = Number.isFinite(n) ? n : null;
                if (details.discount_percent === null) delete details.discount_percent;
            }
            if (values.type === "MOVIE" && typeof details.showtimes === "string") {
                details.showtimes = details.showtimes
                    .split(/\n+/)
                    .map((s) => s.trim())
                    .filter(Boolean)
                    .map(toIsoLocal);
            }
            if (values.type === "MOVIE" && Array.isArray(details.showtimes)) {
                details.showtimes = details.showtimes.filter(Boolean);
            }
            if (values.type === "JOB_FAIR" && typeof details.roles === "string") {
                details.roles = details.roles
                    .split(/[\n,]+/)
                    .map((s) => s.trim())
                    .filter(Boolean);
            }
            for (const k of ["event_datetime", "start_date", "end_date"]) {
                if (details?.[k]) details[k] = toIsoLocal(details[k]);
            }
            for (const k of ["ticket_price", "price_before", "price_after"]) {
                if (details?.[k] != null && details[k] !== "") {
                    details[k] = Number(details[k]);
                    if (!Number.isFinite(details[k])) delete details[k];
                } else {
                    delete details[k];
                }
            }

            const chosenStatus = isStaff ? "DRAFT" : status;

            // Base payload shared by create + edit paths. Keys MUST match the
            // backend contract exactly (see /api/openapi.json):
            // `district_slug`, `category_slug`, `poster_image_url`, `details`.
            const payload = {
                type: values.type,
                title: values.title.trim(),
                description: values.description?.trim() || null,
                district_slug: values.district_slug,
                category_slug: values.category_slug || null,
                valid_from: values.valid_from ? toIsoLocal(values.valid_from) : null,
                valid_to: values.valid_to ? toIsoLocal(values.valid_to) : null,
                details,
                status: chosenStatus,
            };

            // ---- Edit path -------------------------------------------------
            if (editing) {
                let poster_image_url = editing.poster_image_url || null;
                if (file) {
                    const up = await uploadFile(file, "poster");
                    poster_image_url = up.url;
                }
                if (chosenStatus === "PUBLISHED" && !poster_image_url) {
                    toast.error("Poster image is required to publish.");
                    return;
                }
                payload.poster_image_url = poster_image_url;
                const res = await api.patch(`/posts/${editing.id}`, payload);
                toast.success("Post updated.");
                onSubmitted?.(res.data);
                return;
            }

            // ---- Create path ----------------------------------------------
            let created;
            if (file) {
                // One-shot upload + create — matches the mobile-first flow
                // documented on POST /api/posts/upload-and-create.
                created = await uploadAndCreatePost(file, payload);
            } else {
                if (chosenStatus === "PUBLISHED") {
                    toast.error("Poster image is required to publish.");
                    return;
                }
                created = (await api.post("/posts", payload)).data;
            }
            toast.success("Post created.");
            onSubmitted?.(created);
        } catch (e) {
            const detail = e?.response?.data?.detail;
            if (Array.isArray(detail)) {
                toast.error(detail.map((d) => d.msg || JSON.stringify(d)).join("; "));
            } else {
                toast.error(typeof detail === "string" ? detail : "Failed to save post");
            }
        }
    };

    const typeOptions = useMemo(
        () => (isShopOwner ? ["OFFER"] : POST_TYPES),
        [isShopOwner],
    );

    return (
        <form onSubmit={handleSubmit(submit)} className="space-y-6" data-testid="post-form">
            <div className="grid gap-6 lg:grid-cols-5">
                {/* Left: fields */}
                <div className="space-y-6 lg:col-span-3">
                    <section className="rounded-2xl border border-neutral-200 bg-white p-5">
                        <h3 className="mb-4 text-sm font-semibold uppercase tracking-wider text-neutral-500">
                            Basics
                        </h3>
                        <div className="grid gap-4 sm:grid-cols-2">
                            <div>
                                <Label>Post type</Label>
                                <Controller
                                    control={control}
                                    name="type"
                                    render={({ field }) => (
                                        <Select
                                            value={field.value}
                                            onValueChange={field.onChange}
                                            disabled={isShopOwner || !!editing}
                                        >
                                            <SelectTrigger data-testid="post-type-select">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {typeOptions.map((t) => (
                                                    <SelectItem key={t} value={t}>
                                                        {POST_TYPE_LABEL[t]}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    )}
                                />
                            </div>
                            <div className="sm:col-span-2">
                                <Label>Title</Label>
                                <Input
                                    data-testid="post-title-input"
                                    {...register("title", { required: true, maxLength: 120 })}
                                    placeholder="e.g. Weekend Blowout: 50% off Groceries"
                                />
                                {errors.title && (
                                    <p className="mt-1 text-xs text-rose-600">Title is required</p>
                                )}
                            </div>
                            <div className="sm:col-span-2">
                                <Label>Description</Label>
                                <Textarea
                                    data-testid="post-description-input"
                                    rows={3}
                                    {...register("description", { maxLength: 2000 })}
                                    placeholder="Say a bit more about the offer / event."
                                />
                            </div>
                            <div>
                                <Label>District</Label>
                                <Controller
                                    control={control}
                                    name="district_slug"
                                    rules={{ required: true }}
                                    render={({ field }) => (
                                        <Select
                                            value={field.value || undefined}
                                            onValueChange={field.onChange}
                                            disabled={isShopOwner}
                                        >
                                            <SelectTrigger data-testid="post-district-select">
                                                <SelectValue placeholder="Choose district" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {districts.map((d) => (
                                                    <SelectItem key={d.slug} value={d.slug}>
                                                        {d.name}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    )}
                                />
                            </div>
                            <div>
                                <Label>Category</Label>
                                <Controller
                                    control={control}
                                    name="category_slug"
                                    rules={{ required: type === "OFFER" }}
                                    render={({ field }) => (
                                        <Select
                                            value={field.value || undefined}
                                            onValueChange={field.onChange}
                                            disabled={isShopOwner}
                                        >
                                            <SelectTrigger data-testid="post-category-select">
                                                <SelectValue placeholder="Optional (required for offers)" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {categories.map((c) => (
                                                    <SelectItem key={c.slug} value={c.slug}>
                                                        {c.emoji} {c.name}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    )}
                                />
                            </div>
                            <div>
                                <Label>Valid from</Label>
                                <Input
                                    type="datetime-local"
                                    data-testid="post-valid-from"
                                    {...register("valid_from")}
                                />
                            </div>
                            <div>
                                <Label>Valid to</Label>
                                <Input
                                    type="datetime-local"
                                    data-testid="post-valid-to"
                                    {...register("valid_to")}
                                />
                            </div>
                        </div>
                    </section>

                    <TypeSpecific type={type} register={register} control={control} />

                    {type === "OFFER" && (
                        <OfferGallery
                            value={watch("details.gallery") || []}
                            onChange={(g) => setValue("details.gallery", g, { shouldDirty: true })}
                            testidPrefix="post-gallery"
                        />
                    )}

                    {(type === "OFFER" || type === "MOVIE") && (
                        <PartnerLinkSection
                            value={{
                                partner_url: watch("details.partner_url") || "",
                                partner_brand_slug: watch("details.partner_brand_slug") || "",
                            }}
                            onChange={({ partner_url, partner_brand_slug }) => {
                                setValue("details.partner_url", partner_url || "");
                                setValue("details.partner_brand_slug", partner_brand_slug || "");
                            }}
                            postType={type}
                            testidPrefix="post-partner"
                        />
                    )}
                    {type === "MOVIE" && (
                        <YouTubeTrailerSection
                            value={watch("details.trailer_youtube_url") || ""}
                            onChange={(v) => setValue("details.trailer_youtube_url", v)}
                            testidPrefix="post-trailer"
                        />
                    )}
                </div>

                {/* Right: poster + publish */}
                <div className="space-y-6 lg:col-span-2">
                    <section className="rounded-2xl border border-neutral-200 bg-white p-5">
                        <h3 className="mb-4 text-sm font-semibold uppercase tracking-wider text-neutral-500">
                            Poster
                        </h3>
                        {preview ? (
                            <img
                                src={preview}
                                alt="poster preview"
                                className="mb-3 aspect-[4/3] w-full rounded-xl object-cover"
                            />
                        ) : (
                            <div className="mb-3 flex aspect-[4/3] items-center justify-center rounded-xl border-2 border-dashed border-neutral-300 text-neutral-500">
                                <div className="text-center">
                                    <Upload className="mx-auto mb-2" size={22} />
                                    <div className="text-sm">Drop or select an image</div>
                                    <div className="text-xs text-neutral-400">
                                        PNG / JPG / WebP up to 8 MB
                                    </div>
                                </div>
                            </div>
                        )}
                        <input
                            type="file"
                            accept="image/*"
                            onChange={onFileChange}
                            className="block w-full text-sm text-neutral-600 file:mr-3 file:rounded-lg file:border-0 file:bg-neutral-900 file:px-4 file:py-2 file:text-white hover:file:bg-neutral-800"
                            data-testid="post-poster-input"
                        />
                    </section>

                    <section className="rounded-2xl border border-neutral-200 bg-white p-5">
                        <h3 className="mb-4 text-sm font-semibold uppercase tracking-wider text-neutral-500">
                            {isStaff ? "Send for review" : "Publish"}
                        </h3>
                        {isStaff ? (
                            <p className="mb-4 text-sm text-neutral-600">
                                Staff posts are saved as <b>Draft</b> and require a
                                supervisor to publish.
                            </p>
                        ) : (
                            <RadioGroup
                                value={status}
                                onValueChange={setStatus}
                                className="mb-4 grid grid-cols-2 gap-2"
                            >
                                <label
                                    className={`flex cursor-pointer items-center gap-2 rounded-xl border px-3 py-3 text-sm ${status === "DRAFT" ? "border-orange-500 bg-orange-50" : "border-neutral-200"}`}
                                    data-testid="publish-status-draft"
                                >
                                    <RadioGroupItem value="DRAFT" id="s-draft" />
                                    <span>Save as Draft</span>
                                </label>
                                <label
                                    className={`flex cursor-pointer items-center gap-2 rounded-xl border px-3 py-3 text-sm ${status === "PUBLISHED" ? "border-orange-500 bg-orange-50" : "border-neutral-200"}`}
                                    data-testid="publish-status-published"
                                >
                                    <RadioGroupItem value="PUBLISHED" id="s-pub" />
                                    <span>Publish now</span>
                                </label>
                            </RadioGroup>
                        )}
                        <Button
                            type="submit"
                            disabled={isSubmitting}
                            className="w-full nest-gradient text-white hover:opacity-95"
                            data-testid="post-submit-btn"
                        >
                            {isSubmitting ? (
                                <>
                                    <Sparkles className="mr-2 animate-pulse" size={16} />
                                    Saving…
                                </>
                            ) : isStaff ? (
                                <>
                                    <Send className="mr-2" size={16} /> Send for review
                                </>
                            ) : (
                                <>
                                    <Save className="mr-2" size={16} /> Save
                                </>
                            )}
                        </Button>
                    </section>
                </div>
            </div>
        </form>
    );
};

const TypeSpecific = ({ type, register, control }) => {
    const wrap =
        "rounded-2xl border border-neutral-200 bg-white p-5 space-y-4";
    const header = "mb-1 text-sm font-semibold uppercase tracking-wider text-neutral-500";
    if (type === "OFFER") {
        return (
            <section className={wrap}>
                <h3 className={header}>Offer details</h3>
                <div className="grid gap-4 sm:grid-cols-2">
                    <div>
                        <Label>Discount %</Label>
                        <Input
                            type="number"
                            min={0}
                            max={100}
                            data-testid="offer-discount"
                            {...register("details.discount_percent", { valueAsNumber: true })}
                        />
                    </div>
                    <div>
                        <Label>Offer code</Label>
                        <Input
                            data-testid="offer-code"
                            {...register("details.offer_code")}
                            placeholder="ONAM50"
                        />
                    </div>
                    <div>
                        <Label>Price before</Label>
                        <Input
                            type="number"
                            step="0.01"
                            {...register("details.price_before")}
                        />
                    </div>
                    <div>
                        <Label>Price after</Label>
                        <Input
                            type="number"
                            step="0.01"
                            {...register("details.price_after")}
                        />
                    </div>
                    <div className="sm:col-span-2">
                        <Label>Terms</Label>
                        <Textarea rows={2} {...register("details.terms")} />
                    </div>
                </div>
            </section>
        );
    }
    if (type === "MOVIE") {
        return (
            <section className={wrap}>
                <h3 className={header}>Movie details</h3>
                <div className="grid gap-4 sm:grid-cols-2">
                    <div>
                        <Label>Movie title</Label>
                        <Input {...register("details.movie_title", { required: true })} />
                    </div>
                    <div>
                        <Label>Theatre</Label>
                        <Input {...register("details.theatre_name", { required: true })} />
                    </div>
                    <div>
                        <Label>Language</Label>
                        <Input {...register("details.language")} />
                    </div>
                    <div>
                        <Label>Certification</Label>
                        <Input {...register("details.certification")} placeholder="UA" />
                    </div>
                    <div className="sm:col-span-2">
                        <Label>Showtimes (one datetime per line, ISO or local)</Label>
                        <Textarea
                            rows={3}
                            {...register("details.showtimes")}
                            placeholder={"2099-01-01T18:00\n2099-01-01T21:00"}
                        />
                    </div>
                </div>
            </section>
        );
    }
    if (type === "EVENT") {
        return (
            <section className={wrap}>
                <h3 className={header}>Event details</h3>
                <div className="grid gap-4 sm:grid-cols-2">
                    <div className="sm:col-span-2">
                        <Label>Venue</Label>
                        <Input {...register("details.venue", { required: true })} />
                    </div>
                    <div>
                        <Label>Event date &amp; time</Label>
                        <Input
                            type="datetime-local"
                            {...register("details.event_datetime", { required: true })}
                        />
                    </div>
                    <div>
                        <Label>Organizer</Label>
                        <Input {...register("details.organizer")} />
                    </div>
                    <div>
                        <Label>Ticket price</Label>
                        <Input
                            type="number"
                            step="0.01"
                            {...register("details.ticket_price")}
                        />
                    </div>
                </div>
            </section>
        );
    }
    if (type === "JOB_FAIR") {
        return (
            <section className={wrap}>
                <h3 className={header}>Job fair details</h3>
                <div className="grid gap-4 sm:grid-cols-2">
                    <div>
                        <Label>Organizer</Label>
                        <Input {...register("details.organizer", { required: true })} />
                    </div>
                    <div>
                        <Label>Venue</Label>
                        <Input {...register("details.venue", { required: true })} />
                    </div>
                    <div>
                        <Label>Event date &amp; time</Label>
                        <Input
                            type="datetime-local"
                            {...register("details.event_datetime", { required: true })}
                        />
                    </div>
                    <div>
                        <Label>Contact</Label>
                        <Input {...register("details.contact")} />
                    </div>
                    <div className="sm:col-span-2">
                        <Label>Roles (comma or newline separated)</Label>
                        <Textarea rows={2} {...register("details.roles")} />
                    </div>
                </div>
            </section>
        );
    }
    if (type === "EXHIBITION") {
        return (
            <section className={wrap}>
                <h3 className={header}>Exhibition details</h3>
                <div className="grid gap-4 sm:grid-cols-2">
                    <div>
                        <Label>Exhibition type</Label>
                        <Controller
                            control={control}
                            name="details.exhibition_type"
                            rules={{ required: true }}
                            render={({ field }) => (
                                <Select
                                    value={field.value || undefined}
                                    onValueChange={field.onChange}
                                >
                                    <SelectTrigger>
                                        <SelectValue placeholder="Choose type" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {EXHIBITION_TYPES.map((t) => (
                                            <SelectItem key={t} value={t}>
                                                {t.replace(/_/g, " ")}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            )}
                        />
                    </div>
                    <div>
                        <Label>Organizer</Label>
                        <Input {...register("details.organizer")} />
                    </div>
                    <div className="sm:col-span-2">
                        <Label>Venue</Label>
                        <Input {...register("details.venue", { required: true })} />
                    </div>
                    <div>
                        <Label>Start date</Label>
                        <Input
                            type="datetime-local"
                            {...register("details.start_date", { required: true })}
                        />
                    </div>
                    <div>
                        <Label>End date</Label>
                        <Input
                            type="datetime-local"
                            {...register("details.end_date", { required: true })}
                        />
                    </div>
                </div>
            </section>
        );
    }
    return null;
};

export default PostForm;
